import {
  type ByHead,
  formatPaise,
  GST_STATES,
  PERMANENT_REVERSALS,
  REVERSAL_REASONS,
  REVERSE_CHARGE_KINDS,
  REVERSE_CHARGE_RATES,
  type ReversalReason,
  type ReverseChargeKind,
  readRupees,
  reverseChargeTax,
} from '@oneapp/gst';
import type { Gstr3bReturn } from '@oneapp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { businessApi, businessKeys } from '../business/api';
import { Icon } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { Button, Callout, Field } from '../components/ui';
import { ApiError } from '../lib/api';
import { gstr3bApi, gstr3bKeys } from './api';

/** /returns/:id/gstr3b/rcm (Y1) or /returns/:id/gstr3b/reversals (Y2). */
const PATH = /^\/returns\/([\w-]+)\/gstr3b\/(rcm|reversals)\/?$/;

export function isGstr3bPath(path: string): boolean {
  return PATH.test(path);
}

const sum = (h: ByHead) => h.igst + h.cgst + h.sgst + h.cess;

/** Keys for rows that have no id: what each says, numbered when two say the same. */
function rowKeys(labels: readonly string[]): string[] {
  const seen = new Map<string, number>();
  return labels.map((label) => {
    const count = (seen.get(label) ?? 0) + 1;
    seen.set(label, count);
    return count > 1 ? `${label}#${count}` : label;
  });
}

/** How a page tells the chat it's done: saved (carry on), as the other pages do. */
export type Gstr3bFinish = 'saved';

/**
 * GSTR-3B's two questions as pages over the chat (Phase 10): reverse-charge purchases (Y1) and credit
 * to reverse (Y2). Each saves straight away; closing with "continue" tells the chat to carry on.
 */
export function Gstr3bSheet({
  path,
  onClose,
  onFinish,
}: {
  path: string;
  onClose: () => void;
  onFinish: (how: Gstr3bFinish) => void;
}) {
  const { t, i18n } = useTranslation();
  const [, returnId = '', which = 'rcm'] = PATH.exec(path) ?? [];
  const query = useQuery({
    queryKey: gstr3bKeys.return(returnId),
    queryFn: ({ signal }) => gstr3bApi.get(returnId, signal),
  });
  const draft = query.data?.return;
  const month = draft ? monthLabel(draft.period, i18n.language) : '';
  return (
    <Sheet open onClose={onClose} label={draft ? t('gstr3b.sheetLabel', { month }) : t('gstr3b.title')}>
      {!draft ? (
        query.isError ? (
          <Callout tone="error" icon="alert-triangle" role="alert">
            {t('gstr3b.loadFailed')}
          </Callout>
        ) : (
          <p className="chat-status" role="status">
            <span className="spinner spinner--dark" aria-hidden="true" />
          </p>
        )
      ) : which === 'rcm' ? (
        <ReverseChargeForm draft={draft} onDone={() => onFinish('saved')} />
      ) : (
        <ReversalForm draft={draft} onDone={() => onFinish('saved')} />
      )}
    </Sheet>
  );
}

function monthLabel(period: string, language: string): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Saving answers: the return's version guards against a change made elsewhere (the chat, another phone). */
function useSave(draft: Gstr3bReturn) {
  const client = useQueryClient();
  const [problem, setProblem] = useState<'conflict' | 'failed' | 'too_much' | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (work: (version: number) => Promise<{ return: Gstr3bReturn }>) => {
    setBusy(true);
    setProblem(null);
    try {
      const saved = await work(draft.version);
      client.setQueryData(gstr3bKeys.return(draft.id), saved);
      void client.invalidateQueries({ queryKey: gstr3bKeys.computation(draft.id) });
      return saved.return;
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      const code = error.problem?.code;
      if (code === 'VERSION_MISMATCH') {
        setProblem('conflict');
        await client.invalidateQueries({ queryKey: gstr3bKeys.return(draft.id) });
      } else setProblem(code === 'REVERSAL_TOO_LARGE' ? 'too_much' : 'failed');
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { run, problem, busy };
}

function ReverseChargeForm({ draft, onDone }: { draft: Gstr3bReturn; onDone: () => void }) {
  const { t } = useTranslation();
  const formId = useId();
  const business = useQuery({
    queryKey: businessKeys.one(draft.businessId),
    queryFn: ({ signal }) => businessApi.get(draft.businessId, signal),
  });
  const stateName = business.data?.business.stateName ?? GST_STATES[business.data?.business.gstin.slice(0, 2) ?? ''];
  const [kind, setKind] = useState<ReverseChargeKind>('gta');
  const [supplier, setSupplier] = useState('');
  const [amount, setAmount] = useState('');
  const [interState, setInterState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const { run, problem, busy } = useSave(draft);
  const entries = draft.reverseCharge.entries;
  const keys = rowKeys(entries.map((e) => `${e.kind}-${e.taxableValue}-${e.interState}-${e.supplier ?? ''}`));
  const taxableValue = readRupees(amount);
  const igstOnly = REVERSE_CHARGE_RATES[kind].igstOnly || interState;
  const tax = taxableValue !== null ? reverseChargeTax({ kind, taxableValue, interState }) : null;
  const rate = REVERSE_CHARGE_RATES[kind].rate;

  const save = async (andContinue: boolean) => {
    if (taxableValue === null || taxableValue <= 0) {
      // Nothing typed: "continue" carries on with what's there.
      if (andContinue && amount.trim() === '' && entries.length) return onDone();
      setError(t('gstr3b.rcm.errors.amount'));
      return;
    }
    setError(null);
    const next = [
      ...entries.map((e) => ({
        kind: e.kind,
        taxableValue: e.taxableValue,
        interState: e.interState,
        supplier: e.supplier,
      })),
      {
        kind,
        taxableValue,
        interState: REVERSE_CHARGE_RATES[kind].igstOnly ? true : interState,
        supplier: supplier.trim() || null,
      },
    ];
    const saved = await run((version) => gstr3bApi.reverseCharge(draft.id, { entries: next }, version));
    if (!saved) return;
    if (andContinue) return onDone();
    setAdded(t('gstr3b.rcm.added', { what: t(`gstr3b.rcm.kinds.${kind}.label`), amount: formatPaise(taxableValue) }));
    setAmount('');
    setSupplier('');
  };

  const remove = async (index: number) => {
    const next = entries
      .filter((_, i) => i !== index)
      .map((e) => ({ kind: e.kind, taxableValue: e.taxableValue, interState: e.interState, supplier: e.supplier }));
    await run((version) => gstr3bApi.reverseCharge(draft.id, { entries: next }, version));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void save(true);
  };

  return (
    <form id={formId} className="g3b-form" onSubmit={submit} noValidate>
      <div className="g3b-form__head">
        <h2 className="g3b-form__title">{t('gstr3b.rcm.title')}</h2>
        <p className="g3b-form__desc">{t('gstr3b.rcm.desc')}</p>
      </div>
      {problem ? (
        <Callout tone={problem === 'failed' ? 'error' : 'amber'} icon="alert-triangle" role="alert">
          {t(`gstr3b.${problem}`)}
        </Callout>
      ) : null}
      {added ? (
        <Callout tone="green" icon="check-circle" role="status">
          {added}
        </Callout>
      ) : null}
      {entries.length ? (
        <ul className="g3b-list" aria-label={t('gstr3b.rcm.listLabel')}>
          {entries.map((e, i) => (
            <li key={keys[i]} className="g3b-list__row">
              <span className="g3b-list__text">
                <span>{t(`gstr3b.rcm.kinds.${e.kind}.label`)}</span>
                <span className="g3b-list__meta">
                  {formatPaise(e.taxableValue)} · {t('gstr3b.rcm.taxInCash', { amount: formatPaise(sum(e.tax)) })}
                </span>
              </span>
              <button
                type="button"
                className="iconbtn"
                aria-label={t('gstr3b.remove', { what: t(`gstr3b.rcm.kinds.${e.kind}.label`) })}
                onClick={() => void remove(i)}
                disabled={busy}
              >
                <Icon name="x" size={18} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <fieldset className="choice-cards">
        <legend className="section-label">{t('gstr3b.rcm.forWhat')}</legend>
        {REVERSE_CHARGE_KINDS.map((k) => (
          <label key={k} className={`choice-card${kind === k ? ' choice-card--on' : ''}`}>
            <span className="choice-card__text">
              <span className="choice-card__label">{t(`gstr3b.rcm.kinds.${k}.label`)}</span>
              <span className="choice-card__help">{t(`gstr3b.rcm.kinds.${k}.help`)}</span>
            </span>
            <input type="radio" name={`${formId}-kind`} checked={kind === k} onChange={() => setKind(k)} />
          </label>
        ))}
      </fieldset>
      <div className="g3b-form__pair">
        <Field label={t('gstr3b.rcm.supplier')} value={supplier} onChange={setSupplier} />
        <Field
          label={t('gstr3b.rcm.amount')}
          value={amount}
          onChange={setAmount}
          inputMode="numeric"
          error={error ?? undefined}
        />
      </div>
      {REVERSE_CHARGE_RATES[kind].igstOnly ? null : (
        <label className="g3b-check">
          <input type="checkbox" checked={interState} onChange={(e) => setInterState(e.target.checked)} />
          <span>{t('gstr3b.rcm.interState')}</span>
        </label>
      )}
      <div className="g3b-preview" aria-live="polite">
        <span className="g3b-preview__note">
          {REVERSE_CHARGE_RATES[kind].igstOnly
            ? t('gstr3b.rcm.preview.abroad')
            : interState
              ? t('gstr3b.rcm.preview.other')
              : t('gstr3b.rcm.preview.same', { state: stateName ?? t('gstr3b.rcm.yourState') })}
        </span>
        {tax ? (
          <>
            <span className="g3b-preview__row">
              <span>{igstOnly ? `IGST ${rate}%` : `CGST ${rate / 2}% + SGST ${rate / 2}%`}</span>
              <span>{igstOnly ? formatPaise(tax.igst) : `${formatPaise(tax.cgst)} + ${formatPaise(tax.sgst)}`}</span>
            </span>
            <span className="g3b-preview__row g3b-preview__row--total">
              <span>{t('gstr3b.rcm.claimedBack')}</span>
              <span>{formatPaise(sum(tax))}</span>
            </span>
          </>
        ) : null}
      </div>
      <div className="g3b-form__actions">
        <Button variant="secondary" onClick={() => void save(false)} disabled={busy}>
          {t('gstr3b.rcm.addAnother')}
        </Button>
        <Button type="submit" disabled={busy}>
          {t('gstr3b.rcm.addContinue')}
        </Button>
      </div>
    </form>
  );
}

function ReversalForm({ draft, onDone }: { draft: Gstr3bReturn; onDone: () => void }) {
  const { t } = useTranslation();
  const formId = useId();
  const [reason, setReason] = useState<ReversalReason>('blocked');
  const [values, setValues] = useState({ igst: '', cgst: '', sgst: '' });
  const [error, setError] = useState<string | null>(null);
  const { run, problem, busy } = useSave(draft);
  const items = draft.reversals.items;
  const keys = rowKeys(items.map((r) => `${r.reason}-${r.amounts.igst}-${r.amounts.cgst}-${r.amounts.sgst}`));
  // Credit this month: GSTR-2B's, plus reverse-charge credit, less what's already reversed.
  const portal = draft.portal?.credit ?? { igst: 0, cgst: 0, sgst: 0, cess: 0 };
  const rcm = draft.reverseCharge.entries.reduce(
    (n, e) => ({
      igst: n.igst + e.tax.igst,
      cgst: n.cgst + e.tax.cgst,
      sgst: n.sgst + e.tax.sgst,
      cess: n.cess + e.tax.cess,
    }),
    { igst: 0, cgst: 0, sgst: 0, cess: 0 },
  );
  const reversed = items.reduce(
    (n, r) => ({
      igst: n.igst + r.amounts.igst,
      cgst: n.cgst + r.amounts.cgst,
      sgst: n.sgst + r.amounts.sgst,
      cess: n.cess + r.amounts.cess,
    }),
    { igst: 0, cgst: 0, sgst: 0, cess: 0 },
  );
  const available = {
    igst: portal.igst + rcm.igst - reversed.igst,
    cgst: portal.cgst + rcm.cgst - reversed.cgst,
    sgst: portal.sgst + rcm.sgst - reversed.sgst,
    cess: portal.cess + rcm.cess - reversed.cess,
  };
  const read = (text: string) => (text.trim() === '' ? 0 : readRupees(text));
  const amounts = { igst: read(values.igst), cgst: read(values.cgst), sgst: read(values.sgst) };
  const valid = amounts.igst !== null && amounts.cgst !== null && amounts.sgst !== null;
  const total = valid ? (amounts.igst ?? 0) + (amounts.cgst ?? 0) + (amounts.sgst ?? 0) : 0;
  const permanent = PERMANENT_REVERSALS.has(reason);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || total <= 0) {
      setError(t('gstr3b.rev.errors.none'));
      return;
    }
    for (const head of ['igst', 'cgst', 'sgst'] as const) {
      if ((amounts[head] ?? 0) > available[head]) {
        setError(
          t('gstr3b.rev.errors.more', {
            head: head.toUpperCase(),
            available: formatPaise(Math.max(0, available[head])),
          }),
        );
        return;
      }
    }
    setError(null);
    const next = [
      ...items.map((r) => ({ reason: r.reason, amounts: r.amounts })),
      { reason, amounts: { igst: amounts.igst ?? 0, cgst: amounts.cgst ?? 0, sgst: amounts.sgst ?? 0, cess: 0 } },
    ];
    const saved = await run((version) => gstr3bApi.reversals(draft.id, { reversals: next }, version));
    if (saved) onDone();
  };

  const remove = async (index: number) => {
    const next = items.filter((_, i) => i !== index).map((r) => ({ reason: r.reason, amounts: r.amounts }));
    await run((version) => gstr3bApi.reversals(draft.id, { reversals: next }, version));
  };

  return (
    <form id={formId} className="g3b-form" onSubmit={(e) => void submit(e)} noValidate>
      <div className="g3b-form__head">
        <h2 className="g3b-form__title">{t('gstr3b.rev.title')}</h2>
        <p className="g3b-form__desc">
          {t('gstr3b.rev.available', { amount: formatPaise(Math.max(0, sum(available))) })}
        </p>
      </div>
      {problem ? (
        <Callout tone={problem === 'failed' ? 'error' : 'amber'} icon="alert-triangle" role="alert">
          {t(`gstr3b.${problem}`)}
        </Callout>
      ) : null}
      {items.length ? (
        <ul className="g3b-list" aria-label={t('gstr3b.rev.listLabel')}>
          {items.map((r, i) => (
            <li key={keys[i]} className="g3b-list__row">
              <span className="g3b-list__text">
                <span>{t(`gstr3b.rev.reasons.${r.reason}.label`)}</span>
                <span className="g3b-list__meta">{formatPaise(sum(r.amounts))}</span>
              </span>
              <button
                type="button"
                className="iconbtn"
                aria-label={t('gstr3b.remove', { what: t(`gstr3b.rev.reasons.${r.reason}.label`) })}
                onClick={() => void remove(i)}
                disabled={busy}
              >
                <Icon name="x" size={18} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <fieldset className="choice-cards">
        <legend className="section-label">{t('gstr3b.rev.why')}</legend>
        {REVERSAL_REASONS.map((r) => (
          <label key={r} className={`choice-card${reason === r ? ' choice-card--on' : ''}`}>
            <span className="choice-card__text">
              <span className="choice-card__label">{t(`gstr3b.rev.reasons.${r}.label`)}</span>
              <span className="choice-card__help">{t(`gstr3b.rev.reasons.${r}.help`)}</span>
            </span>
            <input type="radio" name={`${formId}-reason`} checked={reason === r} onChange={() => setReason(r)} />
          </label>
        ))}
      </fieldset>
      <div className="g3b-form__heads">
        {(['igst', 'cgst', 'sgst'] as const).map((head) => (
          <Field
            key={head}
            label={head.toUpperCase()}
            value={values[head]}
            onChange={(v) => setValues((s) => ({ ...s, [head]: v }))}
            inputMode="numeric"
          />
        ))}
      </div>
      {error ? (
        <p className="field__help field__help--error" role="alert">
          {error}
        </p>
      ) : null}
      {total > 0 ? (
        <Callout tone="amber" icon="alert-triangle">
          {t(
            permanent
              ? 'gstr3b.rev.effect.permanent'
              : reason === 'rule_37a'
                ? 'gstr3b.rev.effect.rule37a'
                : 'gstr3b.rev.effect.later',
            {
              from: formatPaise(Math.max(0, sum(available))),
              to: formatPaise(Math.max(0, sum(available) - total)),
            },
          )}
        </Callout>
      ) : null}
      <div className="g3b-form__actions">
        <Button type="submit" disabled={busy}>
          {total > 0 ? t('gstr3b.rev.submit', { amount: formatPaise(total) }) : t('gstr3b.rev.submitPlain')}
        </Button>
      </div>
    </form>
  );
}
